import { useState } from 'react';
import { Typography, DialogContent } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import WorkingUnitLocationAutocomplete from '../Selects/WorkingUnitLocationAutocomplete';
import {
    useDispatchErrorNotification,
    useDispatchSuccessNotification,
    useCustomStyles,
    useLocationsByLoggedInUserRole,
    useWorkingUnits,
} from '../../hooks';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import MonthPicker from '../UI/MonthPicker';
import { completeCalculation } from '../../services/completedCalculation';
import { queryClient } from '../../services/queryClient';
import { getCompletedCalculationsQueryKey } from '../../hooks/api/completedCalculations';
import dayjs from '@utils/dayjs';

const getSelectedOptionIds = (selectedRecords, allRecords) => {
    if (selectedRecords.find((r) => r.id === -1)) {
        return allRecords.filter((r) => r.id !== -1).map((r) => r.id);
    }
    return selectedRecords.map((r) => r.id);
};

const CompleteCalculationDialog = ({ open, onClose, dateFilter, workingUnitLocationFilter }) => {
    const { t } = useTranslation('bidding');
    const classes = useCustomStyles();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [locationOptions] = useLocationsByLoggedInUserRole({ prependAllLocationsOption: true });
    const [workingUnitOptions] = useWorkingUnits({ hasMatchingOfferedCluster: true, prependAllOption: true });
    const [selectedWorkingUnits, setSelectedWorkingUnits] = useState([]);

    const { mutate: handleCompleteCalculation } = useMutation({
        mutationFn: completeCalculation,
        onSuccess: () => {
            dispatchSuccessNotification({ message: t('notification.calculationCompleted') });
            onClose();
            setSelectedWorkingUnits([]);
            queryClient.invalidateQueries(
                getCompletedCalculationsQueryKey({ date: dateFilter, workingUnitLocation: workingUnitLocationFilter })
            );
        },
        onError: (error) => {
            dispatchErrorNotification({
                message: error.message || t('notification.calculationFailed'),
            });
        },
    });

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    return (
        <ResponsiveDialog
            fullScreen={false}
            fullWidth={true}
            title={t('completeCalculation.title')}
            onClose={onClose}
            open={open}
            isConfirmationDialog={true}
            confirmButtonText={t('action.yes')}
            closeButtonText={t('action.no')}
            onConfirm={() => {
                handleCompleteCalculation({
                    dateFrom: dayjs.utc(selectedDate).startOf('month'),
                    dateTo: dayjs.utc(selectedDate).endOf('month'),
                    locations: getSelectedOptionIds(selectedLocations, locationOptions),
                    workingUnits: getSelectedOptionIds(selectedWorkingUnits, workingUnitOptions),
                });
            }}
        >
            <DialogContent>
                <WorkingUnitLocationAutocomplete
                    locationOptions={locationOptions}
                    selectedLocations={selectedLocations}
                    onSelectedLocationsChange={setSelectedLocations}
                    workingUnitOptions={workingUnitOptions}
                    selectedWorkingUnits={selectedWorkingUnits}
                    onSelectedWorkingUnitsChange={setSelectedWorkingUnits}
                    isLocationMultiSelect={true}
                    isWorkingUnitMultiSelect={true}
                ></WorkingUnitLocationAutocomplete>
                <MonthPicker date={selectedDate} onDateChange={handleDateChange} sx={classes.marginTop}></MonthPicker>
                <Typography sx={classes.marginTop}>{t('completeCalculation.body')}</Typography>
            </DialogContent>
        </ResponsiveDialog>
    );
};

export default CompleteCalculationDialog;
